'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import WhatsAppIcon from 'components/ui/WhatsAppIcon';
import { NAVIGATION, BRAND } from 'lib/constants';
import { getWhatsAppUrl } from 'lib/whatsapp';
import { cn } from 'lib/utils';
import logoImg from '../../images/logo/singaraa-logo.webp';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = useLocation().pathname;
  const scrollYRef = useRef(0);
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) return;
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  // Lock background scroll completely when mobile sidebar is open
  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY;
      isNavigatingRef.current = false;

      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }
      document.documentElement.style.overflow = 'hidden';

      return () => {
        const savedScrollY = scrollYRef.current;
        const navigating = isNavigatingRef.current;

        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.documentElement.style.overflow = '';

        if (!navigating) {
          window.scrollTo(0, savedScrollY);
        }
      };
    }
  }, [isOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // Global handler for hash links (scroll to element)
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const id = location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.pathname, location.hash]);

  const closeMenu = (navigating = false) => {
    if (navigating) {
      isNavigatingRef.current = true;
    }
    setIsOpen(false);
  };

  // Helper to check if link is active
  const isLinkActive = (href) => {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(`${href}/`);
  };

  return (
    <header
      className={cn(
        'sticky top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-singaraa-ivory/90 backdrop-blur-md shadow-sm border-b border-singaraa-caramel/15 py-0'
          : 'bg-singaraa-ivory py-2'
      )}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20">
        <div className="flex items-center justify-between h-16 md:h-20 transition-all duration-300 relative">

          {/* Left Side: Logo + Nav */}
          <div className="flex items-center gap-10">
            {/* Logo */}
            <Link to="/" className="flex items-center z-10 transition-transform hover:scale-[1.02] active:scale-[0.98]">
              <div className="relative w-auto h-10 sm:h-10 md:h-12 transition-all duration-300">
                <img
                  src={logoImg}
                  alt="Singaraa"
                  width={240}
                  height={64}
                  className="w-auto h-full object-contain"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {NAVIGATION.map((link) => {
                const isActive = isLinkActive(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="relative pt-3.5 flex flex-col items-center group"
                  >
                    <span className={cn(
                      'text-[15px] font-body font-medium transition-colors duration-300',
                      isActive
                        ? 'text-singaraa-brown'
                        : 'text-singaraa-dark/80 hover:text-singaraa-brown'
                    )}>
                      {link.label}
                    </span>
                    {/* Active Indicator Square */}
                    <div className="h-1.5 flex items-center justify-center mt-1">
                      {isActive ? (
                        <motion.div
                          layoutId="nav-indicator"
                          className="w-1.5 h-1.5 bg-singaraa-brown"
                          initial={false}
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      ) : (
                        <div className="w-1.5 h-1.5 bg-transparent" />
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right Side: CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/product#order-now"
              className={cn(
                'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-body font-semibold tracking-wide',
                'bg-[#25D366] text-white hover:bg-[#1da851]',
                'shadow-md shadow-[#25D366]/20 hover:shadow-lg hover:-translate-y-0.5',
                'transition-all duration-300 active:scale-[0.98]'
              )}
            >
              <WhatsAppIcon className="w-4 h-4" />
              Order Now
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => (isOpen ? closeMenu(false) : setIsOpen(true))}
            className="lg:hidden relative z-10 p-2 -mr-2 rounded-lg text-singaraa-dark hover:bg-singaraa-beige/50 transition-colors"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            <div className="relative w-6 h-6 flex items-center justify-center">
              <span className={cn(
                "absolute h-0.5 w-5 bg-current transform transition-all duration-300 ease-in-out",
                isOpen ? "rotate-45" : "-translate-y-1.5"
              )} />
              <span className={cn(
                "absolute h-0.5 w-5 bg-current transform transition-all duration-300 ease-in-out",
                isOpen ? "opacity-0" : "opacity-100"
              )} />
              <span className={cn(
                "absolute h-0.5 w-5 bg-current transform transition-all duration-300 ease-in-out",
                isOpen ? "-rotate-45" : "translate-y-1.5"
              )} />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile Menu via Portal so it escapes header's sticky & backdrop-blur containing block */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isOpen && (
            <div className="lg:hidden">
              {/* Backdrop - touch-none prevents any background dragging/scrolling */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={() => closeMenu(false)}
                onTouchMove={(e) => e.preventDefault()}
                className="fixed inset-0 bg-singaraa-dark/60 backdrop-blur-sm z-[9999] touch-none"
              />
              {/* Sidebar */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                className="fixed top-0 right-0 h-[100dvh] w-[300px] max-w-[85vw] bg-singaraa-ivory shadow-2xl z-[10000] flex flex-col justify-between overflow-hidden overscroll-contain"
              >
                {/* Header with Logo & Close Button - touch-none */}
                <div
                  className="flex items-center justify-between h-16 px-5 border-b border-singaraa-caramel/15 bg-singaraa-ivory shrink-0 touch-none"
                  onTouchMove={(e) => e.preventDefault()}
                >
                  <Link
                    to="/"
                    onClick={() => closeMenu(true)}
                    className="relative w-auto h-8 flex items-center"
                  >
                    <img
                      src={logoImg}
                      alt="Singaraa"
                      width={240}
                      height={64}
                      className="w-auto h-full object-contain"
                    />
                  </Link>
                  <button
                    onClick={() => closeMenu(false)}
                    className="p-2 -mr-2 rounded-xl text-singaraa-dark hover:bg-singaraa-beige/50 transition-colors"
                    aria-label="Close menu"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Nav Links - touch-pan-y and overscroll-contain prevent scroll-chaining to page */}
                <div className="flex-1 py-4 px-4 overscroll-contain touch-pan-y flex flex-col gap-1.5">
                  {NAVIGATION.map((link, i) => {
                    const isActive = isLinkActive(link.href);
                    return (
                      <motion.div
                        key={link.href}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 + i * 0.04 }}
                      >
                        <Link
                          to={link.href}
                          onClick={() => closeMenu(true)}
                          className={cn(
                            "flex items-center justify-between px-4 py-3 text-base font-body font-medium rounded-xl transition-all",
                            isActive
                              ? "bg-singaraa-beige text-singaraa-brown font-semibold shadow-sm"
                              : "text-singaraa-dark/80 hover:text-singaraa-brown hover:bg-singaraa-beige/50"
                          )}
                        >
                          {link.label}
                          {isActive && (
                            <div className="w-2 h-2 rounded-full bg-singaraa-brown" />
                          )}
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Bottom CTA Action Buttons - touch-none */}
                <div
                  className="p-4 sm:p-5 border-t border-singaraa-caramel/15 bg-white/95 backdrop-blur-sm flex flex-col gap-2.5 shrink-0 touch-none"
                  onTouchMove={(e) => e.preventDefault()}
                >
                  <Link
                    to="/product#order-now"
                    onClick={() => closeMenu(true)}
                    className={cn(
                      'flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-xl text-sm font-body font-semibold',
                      'bg-[#25D366] text-white hover:bg-[#1da851]',
                      'shadow-md shadow-[#25D366]/20',
                      'transition-all duration-300 active:scale-[0.98]'
                    )}
                  >
                    <WhatsAppIcon className="w-5 h-5" />
                    Order Fresh Peanuts
                  </Link>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
